import moment from 'moment';

/** Form / Excel labels mapped to API `onTimeFlag` (1 = on time, 0 = late). */
export const FABRICATION_ON_TIME_FLAG_OPTIONS = [
  { value: 1, label: 'On Time' },
  { value: 0, label: 'Late' },
];

/** @deprecated use FABRICATION_ON_TIME_FLAG_OPTIONS */
export const FABRICATION_DELIVERY_STATUS_OPTIONS = FABRICATION_ON_TIME_FLAG_OPTIONS;

export const normalizeDeliveryStatus = (value) => {
  if (value == null || value === '') return null;
  const s = String(value).trim();
  if (/^on\s*time$/i.test(s)) return 'On Time';
  if (/^late$/i.test(s)) return 'Late';
  return s;
};

export const onTimeFlagFromDeliveryStatusLabel = (value) => {
  const label = normalizeDeliveryStatus(value);
  if (label === 'On Time') return 1;
  if (label === 'Late') return 0;
  return null;
};

export const deliveryStatusFromOnTimeFlag = (onTimeFlag) => {
  if (onTimeFlag === 1) return 'On Time';
  if (onTimeFlag === 0) return 'Late';
  return null;
};

const toNumber = (v) => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const toMoment = (v) => {
  if (v == null || v === '') return null;
  const m = moment(v);
  return m.isValid() ? m.startOf('day') : null;
};

/**
 * Derived metrics for a fabrication job row (Excel parity).
 * @param {object} job - stored fields only
 * @returns {object} derived fields
 */
export const computeFabricationDerivedFields = (job = {}) => {
  const quotedHours = toNumber(job.quotedHours);
  const actualHours = toNumber(job.actualHours);
  const totalWeldedJoints = toNumber(job.totalWeldedJoints);
  const totalJointsRejected = toNumber(job.totalJointsRejected);

  const hoursVariance =
    quotedHours != null && actualHours != null ? quotedHours - actualHours : null;

  let hrsVariancePct = null;
  if (quotedHours != null && actualHours != null) {
    hrsVariancePct = quotedHours !== 0 ? hoursVariance / quotedHours : 0;
  }

  const promised = toMoment(job.promisedDeliveryDate);
  const actual = toMoment(job.actualDeliveryDate);

  let onTimeFlag =
    job.onTimeFlag != null && job.onTimeFlag !== '' ? Number(job.onTimeFlag) : null;
  if (onTimeFlag != null && !Number.isFinite(onTimeFlag)) {
    onTimeFlag = null;
  }

  let deliveryStatus = normalizeDeliveryStatus(job.deliveryStatus);
  let deliveryDaysVariance = null;

  if (onTimeFlag === 1) {
    deliveryStatus = 'On Time';
  } else if (onTimeFlag === 0) {
    deliveryStatus = 'Late';
  }

  if (promised && actual) {
    deliveryDaysVariance = actual.diff(promised, 'days');
    if (!deliveryStatus) {
      deliveryStatus = actual.isSameOrBefore(promised, 'day') ? 'On Time' : 'Late';
    }
  }

  if (onTimeFlag == null) {
    if (deliveryStatus === 'On Time') {
      onTimeFlag = 1;
    } else if (deliveryStatus === 'Late') {
      onTimeFlag = 0;
    }
  }

  let rejectRatePct = null;
  if (totalWeldedJoints != null && totalJointsRejected != null) {
    rejectRatePct = totalWeldedJoints !== 0 ? totalJointsRejected / totalWeldedJoints : 0;
  }

  return {
    hoursVariance,
    hrsVariancePct,
    deliveryStatus,
    onTimeFlag,
    deliveryDaysVariance,
    rejectRatePct,
  };
};

/** Merge stored job with derived fields for display. */
export const enrichFabricationJob = (job) => ({
  ...job,
  ...computeFabricationDerivedFields(job),
});

export const formatPercent = (value, fractionDigits = 0) => {
  if (value == null || Number.isNaN(value)) return '—';
  return `${(value * 100).toFixed(fractionDigits)}%`;
};

export const formatDateDisplay = (value) => {
  if (value == null || value === '') return '—';
  const m = moment(value);
  return m.isValid() ? m.format('DD MMM YYYY') : '—';
};

export const isNegativeHighlight = (field, value) => {
  if (value == null || Number.isNaN(value)) return false;
  if (field === 'hrsVariancePct' || field === 'deliveryDaysVariance') return value < 0;
  return false;
};
